import { Badge } from "@/components/ui/badge";
import { Star, Sun, Moon, AlertTriangle, ThumbsUp, ThumbsDown } from "lucide-react";

export const IngredientCard = ({ ingredient }) => {
  const rating = ingredient.rating || 3;
  
  const getRatingColor = (r) => {
    if (r >= 4) return "ingredient-excellent";
    if (r === 3) return "ingredient-neutral";
    if (r === 2) return "ingredient-caution";
    return "ingredient-avoid";
  };

  const getRatingLabel = (r) => {
    if (r >= 5) return "Excellent";
    if (r === 4) return "Good";
    if (r === 3) return "Neutral";
    if (r === 2) return "Caution";
    return "Avoid";
  };

  const renderStars = (count) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-3 h-3 ${i < count ? "fill-amber-400 text-amber-400" : "text-gray-300"}`}
      />
    ));
  };

  return (
    <div 
      className="bg-white/60 border border-border/50 rounded-xl p-4 hover:bg-white/80 transition-colors"
      data-testid={`ingredient-${ingredient.name?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <h4 className="font-medium text-primary text-sm">{ingredient.name}</h4>
          {ingredient.inci_name && ingredient.inci_name !== ingredient.name && (
            <p className="text-xs text-muted-foreground italic">{ingredient.inci_name}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Rating Stars */}
          <div className="flex items-center gap-0.5">
            {renderStars(rating)}
          </div>
          {/* Rating Badge */}
          <Badge className={`text-xs ${getRatingColor(rating)}`}>
            {getRatingLabel(rating)}
          </Badge>
        </div>
      </div>

      {/* Function */}
      {ingredient.function && (
        <p className="text-xs text-muted-foreground mb-2">
          <span className="font-medium">Function:</span> {ingredient.function}
        </p>
      )}

      {/* Best Time */}
      {ingredient.best_time && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-muted-foreground">Best used:</span>
          <Badge 
            variant="outline" 
            className={`text-xs ${
              ingredient.best_time === "AM" ? "badge-am" : 
              ingredient.best_time === "PM" ? "badge-pm" : 
              "badge-both"
            }`}
          >
            {ingredient.best_time === "AM" && <Sun className="w-3 h-3 mr-1" />}
            {ingredient.best_time === "PM" && <Moon className="w-3 h-3 mr-1" />}
            {ingredient.best_time}
          </Badge>
        </div>
      )}

      {/* Benefits */}
      {ingredient.benefits && ingredient.benefits.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-1 mb-1">
            <ThumbsUp className="w-3 h-3 text-primary" />
            <span className="text-xs font-medium text-primary">Benefits</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {ingredient.benefits.slice(0, 3).map((benefit, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs bg-primary/10 text-primary">
                {benefit}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Concerns */}
      {ingredient.concerns && ingredient.concerns.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-1 mb-1">
            <AlertTriangle className="w-3 h-3 text-accent" />
            <span className="text-xs font-medium text-accent">Concerns</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {ingredient.concerns.slice(0, 2).map((concern, idx) => (
              <Badge key={idx} variant="outline" className="text-xs border-accent/30 text-accent">
                {concern}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Avoid With */}
      {ingredient.avoid_with && ingredient.avoid_with.length > 0 && (
        <div>
          <div className="flex items-center gap-1 mb-1">
            <ThumbsDown className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Avoid with</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {ingredient.avoid_with.slice(0, 3).join(", ")}
          </p>
        </div>
      )}
    </div>
  );
};

export default IngredientCard;
